import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConnectorsIncidents } from 'Services/connectorsHealth';
import { trackError } from 'Utils/errorMonitoring';
import { CONNECTOR_HEALTH_STATUSES } from 'Utils/constants';

export const CONNECTORS_INCIDENTS_QUERY_KEY = ['connectors-incidents'];

const REFETCH_INTERVAL = 5 * 60 * 1000;

/**
 * Connectors that are not healthy right now. It is refreshed every few minutes, so it is more
 * up to date than the `connector` of each connection, which is only loaded once.
 */
export default function useConnectorsIncidents() {
    const { data: incidents = [], isSuccess } = useQuery({
        queryKey: CONNECTORS_INCIDENTS_QUERY_KEY,
        queryFn: getConnectorsIncidents,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        staleTime: 60 * 1000,
        onError: (error) => trackError(error),
    });

    const incidentsByConnectorId = useMemo(
        () => new Map(incidents.map((incident) => [incident.connector_id, incident])),
        [incidents],
    );

    const getConnectorHealth = useCallback(
        (connector) => {
            if (!connector) {
                return { status: CONNECTOR_HEALTH_STATUSES.HEALTHY, message: null, since: null };
            }

            const base = { name: connector.name, service: connector.service };

            if (!isSuccess) {
                return {
                    ...base,
                    status: connector.health_status ?? CONNECTOR_HEALTH_STATUSES.HEALTHY,
                    message: connector.health_status_message ?? null,
                    since: null,
                };
            }

            const incident = incidentsByConnectorId.get(connector.id);

            return {
                ...base,
                status: incident?.health_status ?? CONNECTOR_HEALTH_STATUSES.HEALTHY,
                message: incident?.health_status_message ?? null,
                since: incident?.health_status_since ?? null,
            };
        },
        [incidentsByConnectorId, isSuccess],
    );

    const isConnectorBroken = useCallback(
        (connector) => getConnectorHealth(connector).status === CONNECTOR_HEALTH_STATUSES.BROKEN,
        [getConnectorHealth],
    );

    return { incidents, isSuccess, getConnectorHealth, isConnectorBroken };
}
